import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Tab, Tabs, Avatar,
  List, ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction,
  IconButton, Divider, Alert, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel,
  Select, MenuItem, LinearProgress, Tooltip, Table, TableBody,
  TableCell, TableHead, TableRow, Collapse
} from '@mui/material';
import {
  ArrowBack, Edit, Upload, Delete, Download, Description,
  Badge, Gavel, School, FlightTakeoff, Receipt, Folder,
  Email, Phone, Business, Person, CalendarToday, Add,
  ExpandMore, ExpandLess, AttachMoney, CreditCard, Close
} from '@mui/icons-material';
import EmployeeIdCard from '../../components/EmployeeIdCard';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { apiFetch } from '../../services/apiFetch';
import { displayName } from '../../types';
import EditEmployeeModal from '../../components/EditEmployeeModal';
import { CURRENCIES } from '../../data/constants';

const CATEGORIES = [
  { value: 'id', label: 'ID Document', icon: <Badge />, color: '#1976d2' },
  { value: 'contract', label: 'Contract', icon: <Gavel />, color: '#7b1fa2' },
  { value: 'certificate', label: 'Certificate', icon: <School />, color: '#388e3c' },
  { value: 'visa', label: 'Visa / Work Permit', icon: <FlightTakeoff />, color: '#f57c00' },
  { value: 'payslip', label: 'Payslip', icon: <Receipt />, color: '#0288d1' },
  { value: 'other', label: 'Other', icon: <Folder />, color: '#616161' },
];

const getCategoryInfo = (value: string) => CATEGORIES.find(c => c.value === value) || CATEGORIES[5];
const formatSize = (bytes: number) => bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const EmployeeProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadCategory, setUploadCategory] = useState('other');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Contract tab state
  const [contracts, setContracts] = useState<any[]>([]);
  const [compensation, setCompensation] = useState<any[]>([]);
  const [contractHistoryOpen, setContractHistoryOpen] = useState(false);
  const [compHistoryOpen, setCompHistoryOpen] = useState(false);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [compDialogOpen, setCompDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<any>(null);
  const [editingComp, setEditingComp] = useState<any>(null);
  const [contractForm, setContractForm] = useState({
    contractType: 'full_time', startDate: '', endDate: '',
    weeklyHours: 40, probationMonths: 3, noticePeriodDays: 30,
  });
  const [compForm, setCompForm] = useState({
    compensationType: 'salary', currency: 'HKD', amount: '',
    bonusTarget: '', effectiveFrom: new Date().toISOString().split('T')[0],
  });
  const [savingContract, setSavingContract] = useState(false);
  const [savingComp, setSavingComp] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(false);
  const [idCardOpen, setIdCardOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      apiService.getEmployees(),
      apiService.getEmployeeDocuments(id).catch(() => []),
      apiService.getLeaveRequests().catch(() => []),
      apiService.getEmployeeLegacyContracts(id).catch(() => []),
      apiService.getEmployeeCompensation(id).catch(() => []),
    ]).then(([emps, docs, leaves, ctrs, comps]) => {
      const emp = emps.find((e: any) => e.id === id);
      setEmployee(emp || null);
      setDocuments(docs);
      setLeaveRequests(leaves.filter((l: any) => l.employeeId === id));
      setContracts(ctrs);
      setCompensation(comps);
      setLoading(false);
    }).catch(() => { setError('Failed to load profile'); setLoading(false); });
  }, [id]);

  const loadContractData = () => {
    if (!id) return;
    apiService.getEmployeeLegacyContracts(id).then(setContracts).catch(() => {});
    apiService.getEmployeeCompensation(id).then(setCompensation).catch(() => {});
  };

  const handleSaveContract = async () => {
    if (!id) return;
    setSavingContract(true);
    try {
      if (editingContract) {
        await apiService.updateEmployeeLegacyContract(id, editingContract.id, contractForm);
      } else {
        await apiService.createEmployeeLegacyContract(id, contractForm);
      }
      setContractDialogOpen(false);
      setEditingContract(null);
      loadContractData();
      setSuccess('Contract saved!');
    } catch { setError('Failed to save contract.'); }
    finally { setSavingContract(false); }
  };

  const handleSaveComp = async () => {
    if (!id) return;
    setSavingComp(true);
    try {
      if (editingComp) {
        await apiService.updateEmployeeCompensation(id, editingComp.id, compForm);
      } else {
        await apiService.createEmployeeCompensation(id, { ...compForm, amount: Number(compForm.amount), bonusTarget: compForm.bonusTarget ? Number(compForm.bonusTarget) : null });
      }
      setCompDialogOpen(false);
      setEditingComp(null);
      loadContractData();
      setSuccess('Compensation saved!');
    } catch { setError('Failed to save compensation.'); }
    finally { setSavingComp(false); }
  };

  const openContractDialog = (contract?: any) => {
    if (contract) {
      setEditingContract(contract);
      setContractForm({
        contractType: contract.contractType,
        startDate: contract.startDate?.split('T')[0] || '',
        endDate: contract.endDate?.split('T')[0] || '',
        weeklyHours: contract.weeklyHours,
        probationMonths: contract.probationMonths,
        noticePeriodDays: contract.noticePeriodDays,
      });
    } else {
      setEditingContract(null);
      setContractForm({ contractType: 'full_time', startDate: '', endDate: '', weeklyHours: 40, probationMonths: 3, noticePeriodDays: 30 });
    }
    setContractDialogOpen(true);
  };

  const openCompDialog = (comp?: any) => {
    if (comp) {
      setEditingComp(comp);
      setCompForm({
        compensationType: comp.compensationType,
        currency: comp.currency,
        amount: comp.amount,
        bonusTarget: comp.bonusTarget || '',
        effectiveFrom: comp.effectiveFrom?.split('T')[0] || '',
      });
    } else {
      setEditingComp(null);
      setCompForm({ compensationType: 'salary', currency: 'HKD', amount: '', bonusTarget: '', effectiveFrom: new Date().toISOString().split('T')[0] });
    }
    setCompDialogOpen(true);
  };

  const loadDocuments = () => {
    if (!id) return;
    apiService.getEmployeeDocuments(id).then(setDocuments).catch(() => {});
  };

  const handleUpload = async () => {
    if (!uploadFile || !id) return;
    setUploading(true);
    try {
      await apiService.uploadDocument(id, uploadFile, uploadCategory, uploadDescription);
      setSuccess('File uploaded!');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadDescription('');
      loadDocuments();
    } catch { setError('Upload failed.'); }
    finally { setUploading(false); }
  };

  const handleResendInvite = async () => {
    if (!employee) return;
    setError('');
    setSuccess('');
    try {
      setResendingInvite(true);
      const result = await apiService.sendInviteForEmployee(employee.id);
      setSuccess(`Invite link ready: ${result.inviteUrl}`);
    } catch { setError('Failed to send invite.'); }
    finally { setResendingInvite(false); }
  };

  const handleDelete = async (docId: string, name: string) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await apiService.deleteDocument(docId);
      setSuccess('Deleted.');
      loadDocuments();
    } catch { setError('Delete failed.'); }
  };

  const handleDownload = async (docId: string, originalName: string) => {
    try {
      const url = apiService.getDocumentDownloadUrl(docId);
      const response = await apiFetch(url);
      if (!response.ok) throw new Error(`Download failed (HTTP ${response.status})`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = originalName;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      alert(`Failed to download document: ${err?.message || 'Unknown error'}`);
    }
  };

  const filteredDocs = documents.filter(d => !filterCategory || d.category === filterCategory);

  if (loading) return <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>;
  if (!employee) return <Alert severity="error">Employee not found</Alert>;

  const initials = `${(employee.nickname || employee.firstName)?.[0] || ''}${employee.lastName?.[0] || ''}`;
  const statusColor: Record<string, any> = { active: 'success', terminated: 'error', on_leave: 'warning' };

  return (
    <Box>
      {/* Back + Header */}
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <IconButton onClick={() => navigate('/employees')}><ArrowBack /></IconButton>
        <Typography variant="h5" fontWeight="bold">Employee Profile</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Profile Header Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={3}>
          <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: 28, fontWeight: 'bold' }}>{initials}</Avatar>
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h5" fontWeight="bold">{displayName(employee)}</Typography>
              <Chip label={employee.employmentStatus} color={statusColor[employee.employmentStatus] || 'default'} size="small" />
              {employee.workMode && <Chip label={employee.workMode === 'onsite' ? 'Onsite' : 'Remote'} size="small" variant="outlined" />}
            </Box>
            <Typography color="text.secondary">{employee.position?.title || '—'}</Typography>
            <Box display="flex" gap={3} mt={1} flexWrap="wrap">
              {employee.department && <Box display="flex" alignItems="center" gap={0.5}><Business fontSize="small" color="action" /><Typography variant="body2">{employee.department.name}</Typography></Box>}
              {employee.email && <Box display="flex" alignItems="center" gap={0.5}><Email fontSize="small" color="action" /><Typography variant="body2">{employee.email}</Typography></Box>}
              {employee.phoneNumber && <Box display="flex" alignItems="center" gap={0.5}><Phone fontSize="small" color="action" /><Typography variant="body2">{employee.phoneNumber}</Typography></Box>}
              {employee.startDate && <Box display="flex" alignItems="center" gap={0.5}><CalendarToday fontSize="small" color="action" /><Typography variant="body2">Joined {new Date(employee.startDate).toLocaleDateString()}</Typography></Box>}
              {employee.manager && <Box display="flex" alignItems="center" gap={0.5}><Person fontSize="small" color="action" /><Typography variant="body2">Reports to {displayName(employee.manager)}</Typography></Box>}
            </Box>
          </Box>
          <Button variant="outlined" startIcon={<Edit />} onClick={() => setIsEditModalOpen(true)}>Edit</Button>
          <Button variant="outlined" startIcon={<CreditCard />} onClick={() => setIdCardOpen(true)}>ID Card</Button>
          <Button variant="outlined" startIcon={<Email />} onClick={handleResendInvite} disabled={resendingInvite}>
            {resendingInvite ? 'Sending…' : 'Resend Invite'}
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Details" />
          <Tab label={`Documents (${documents.length})`} />
          <Tab label={`Leave (${leaveRequests.length})`} />
          <Tab label="Contract & Salary" />
        </Tabs>

        <Box p={3}>
          {/* Details Tab */}
          {tab === 0 && (() => {
            const activeComp = compensation.find(c => c.isActive);
            return (
              <Grid container spacing={3}>
                {[
                  { label: 'Employee Number', value: employee.employeeNumber },
                  { label: 'Name', value: displayName(employee) },
                  { label: 'Email', value: employee.email },
                  { label: 'Phone', value: employee.phoneNumber },
                  { label: 'Date of Birth', value: employee.dateOfBirth ? new Date(employee.dateOfBirth).toLocaleDateString() : null },
                  { label: 'Department', value: employee.department?.name },
                  { label: 'Position', value: employee.position?.title },
                  { label: 'Manager', value: displayName(employee.manager) },
                  { label: 'Start Date', value: employee.startDate ? new Date(employee.startDate).toLocaleDateString() : null },
                  { label: 'Employment Status', value: employee.employmentStatus },
                  { label: 'Access', value: employee.access ? 'Approver' : null },
                  { label: 'Entity', value: employee.entity?.name },
                  { label: 'Address', value: employee.address },
                  { label: 'Emergency Contact', value: employee.emergencyContactName },
                  { label: 'Emergency Phone', value: employee.emergencyContactPhone },
                  { label: 'Work Mode', value: employee.workMode ? (employee.workMode === 'onsite' ? 'Onsite' : 'Remote') : null },
                ].map(({ label, value }) => value ? (
                  <Grid key={label} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                    <Typography variant="body2" fontWeight="medium">{value}</Typography>
                  </Grid>
                ) : null)}

                {(employee.monthlySalary || activeComp) && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary" display="block">Monthly Salary</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {employee.monthlySalary
                        ? `${employee.salaryCurrency || 'HKD'} ${Number(employee.monthlySalary).toLocaleString()}`
                        : activeComp ? `${activeComp.currency} ${Number(activeComp.amount).toLocaleString()}` : '-'}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            );
          })()}

          {/* Documents Tab */}
          {tab === 1 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {CATEGORIES.filter(cat => documents.some(d => d.category === cat.value)).map(cat => (
                    <Chip key={cat.value} size="small" icon={cat.icon}
                      label={`${cat.label} (${documents.filter(d => d.category === cat.value).length})`}
                      onClick={() => setFilterCategory(filterCategory === cat.value ? '' : cat.value)}
                      variant={filterCategory === cat.value ? 'filled' : 'outlined'}
                      sx={{ borderColor: cat.color, color: filterCategory === cat.value ? 'white' : cat.color, bgcolor: filterCategory === cat.value ? cat.color : 'transparent' }}
                    />
                  ))}
                </Box>
                <Button variant="contained" size="small" startIcon={<Upload />} onClick={() => setUploadOpen(true)}>Upload</Button>
              </Box>

              {/* Drop zone */}
              <Box
                sx={{ border: '2px dashed', borderColor: dragOver ? 'primary.main' : '#e0e0e0', borderRadius: 2, p: 2, textAlign: 'center', mb: 2, cursor: 'pointer', transition: 'all 0.2s' }}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) { setUploadFile(f); setUploadOpen(true); } }}
                onClick={() => setUploadOpen(true)}
              >
                <Typography variant="caption" color="text.secondary"><Upload fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />Drag & drop files here or click to upload</Typography>
              </Box>

              {filteredDocs.length === 0 ? (
                <Box textAlign="center" py={4} color="text.secondary">
                  <Folder sx={{ fontSize: 48, opacity: 0.3 }} />
                  <Typography mt={1}>No documents yet</Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {filteredDocs.map((doc, i) => {
                    const cat = getCategoryInfo(doc.category);
                    return (
                      <React.Fragment key={doc.id}>
                        {i > 0 && <Divider />}
                        <ListItem sx={{ py: 1.5, '&:hover': { bgcolor: '#f9f9f9' } }}>
                          <ListItemIcon><Box sx={{ color: cat.color }}>{cat.icon}</Box></ListItemIcon>
                          <ListItemText
                            primary={<Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" fontWeight="medium">{doc.originalName}</Typography>
                              <Chip label={cat.label} size="small" sx={{ fontSize: 10, height: 18, bgcolor: cat.color + '20', color: cat.color }} />
                            </Box>}
                            secondary={`${doc.description ? doc.description + ' · ' : ''}${formatSize(doc.fileSize)} · ${new Date(doc.createdAt).toLocaleDateString()}`}
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title="Download"><IconButton size="small" onClick={() => handleDownload(doc.id, doc.originalName)}><Download fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(doc.id, doc.originalName)}><Delete fontSize="small" /></IconButton></Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
          )}

          {/* Leave Tab */}
          {tab === 2 && (
            <Box>
              {leaveRequests.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={4}>No leave requests</Typography>
              ) : (
                <List disablePadding>
                  {leaveRequests.map((l, i) => (
                    <React.Fragment key={l.id}>
                      {i > 0 && <Divider />}
                      <ListItem sx={{ py: 1.5 }}>
                        <ListItemText
                          primary={`${l.leaveType?.name || l.type} — ${new Date(l.startDate).toLocaleDateString()} to ${new Date(l.endDate).toLocaleDateString()}`}
                          secondary={l.reason || 'No reason provided'}
                        />
                        <Chip label={l.status} size="small"
                          color={l.status === 'approved' ? 'success' : l.status === 'pending' ? 'warning' : 'error'} />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* Contract & Salary Tab */}
          {tab === 3 && (() => {
            const activeContract = contracts.find(c => c.isActive);
            const pastContracts = contracts.filter(c => !c.isActive);
            const activeComp = compensation.find(c => c.isActive);
            const pastComp = compensation.filter(c => !c.isActive);
            const contractDocs = documents.filter(d => d.category === 'contract');

            return (
              <Box display="flex" flexDirection="column" gap={3}>

                {/* Employment Contract Section */}
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">Employment Contract</Typography>
                    <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => openContractDialog()}>Add Contract</Button>
                  </Box>
                  {activeContract ? (
                    <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Grid container spacing={2}>
                          {[
                            { label: 'Contract Type', value: activeContract.contractType?.replace('_', ' ').toUpperCase() },
                            { label: 'Start Date', value: activeContract.startDate ? new Date(activeContract.startDate).toLocaleDateString() : '—' },
                            { label: 'End Date', value: activeContract.endDate ? new Date(activeContract.endDate).toLocaleDateString() : 'Ongoing' },
                            { label: 'Weekly Hours', value: activeContract.weeklyHours ? `${activeContract.weeklyHours} hrs` : '—' },
                            { label: 'Probation', value: activeContract.probationMonths ? `${activeContract.probationMonths} months` : '—' },
                            { label: 'Notice Period', value: activeContract.noticePeriodDays ? `${activeContract.noticePeriodDays} days` : '—' },
                          ].map(({ label, value }) => (
                            <Grid key={label} size={{ xs: 6, sm: 4 }}>
                              <Typography variant="caption" color="text.secondary">{label}</Typography>
                              <Typography variant="body2" fontWeight="medium">{value}</Typography>
                            </Grid>
                          ))}
                        </Grid>
                        <IconButton size="small" onClick={() => openContractDialog(activeContract)}><Edit fontSize="small" /></IconButton>
                      </Box>
                    </Paper>
                  ) : (
                    <Typography color="text.secondary" py={1}>No active contract on record.</Typography>
                  )}

                  {pastContracts.length > 0 && (
                    <Box>
                      <Button size="small" onClick={() => setContractHistoryOpen(o => !o)} endIcon={contractHistoryOpen ? <ExpandLess /> : <ExpandMore />} sx={{ mb: 1 }}>
                        Contract History ({pastContracts.length})
                      </Button>
                      <Collapse in={contractHistoryOpen}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Type</TableCell>
                              <TableCell>Start</TableCell>
                              <TableCell>End</TableCell>
                              <TableCell>Hours/wk</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pastContracts.map(c => (
                              <TableRow key={c.id}>
                                <TableCell>{c.contractType?.replace('_', ' ')}</TableCell>
                                <TableCell>{c.startDate ? new Date(c.startDate).toLocaleDateString() : '—'}</TableCell>
                                <TableCell>{c.endDate ? new Date(c.endDate).toLocaleDateString() : '—'}</TableCell>
                                <TableCell>{c.weeklyHours}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </Box>
                  )}
                </Box>

                <Divider />

                {/* Compensation Section */}
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">Compensation</Typography>
                    <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => openCompDialog()}>Add Package</Button>
                  </Box>
                  {activeComp ? (
                    <Paper variant="outlined" sx={{ p: 2, mb: 1, borderColor: 'success.light' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <AttachMoney color="success" />
                            <Typography variant="h5" fontWeight="bold" color="success.main">
                              {activeComp.currency} {Number(activeComp.amount).toLocaleString()}
                            </Typography>
                            <Chip size="small" label={activeComp.compensationType === 'salary' ? 'Monthly Salary' : 'Hourly Rate'} color="success" variant="outlined" />
                            <Chip size="small" label={activeComp.currency} variant="outlined" />
                          </Box>
                          {activeComp.compensationType === 'salary' && (
                            <Typography variant="caption" color="text.secondary">
                              Annual: {activeComp.currency} {(Number(activeComp.amount) * 12).toLocaleString()}
                            </Typography>
                          )}
                          {activeComp.bonusTarget && (
                            <Typography variant="body2" color="text.secondary" mt={0.5}>
                              Bonus Target: {activeComp.currency} {Number(activeComp.bonusTarget).toLocaleString()}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                            Effective from: {activeComp.effectiveFrom ? new Date(activeComp.effectiveFrom).toLocaleDateString() : '—'}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => openCompDialog(activeComp)}><Edit fontSize="small" /></IconButton>
                      </Box>
                    </Paper>
                  ) : (
                    <Typography color="text.secondary" py={1}>No compensation package on record.</Typography>
                  )}

                  {pastComp.length > 0 && (
                    <Box>
                      <Button size="small" onClick={() => setCompHistoryOpen(o => !o)} endIcon={compHistoryOpen ? <ExpandLess /> : <ExpandMore />} sx={{ mb: 1 }}>
                        Compensation History ({pastComp.length})
                      </Button>
                      <Collapse in={compHistoryOpen}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Type</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Currency</TableCell>
                              <TableCell>Effective From</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pastComp.map(c => (
                              <TableRow key={c.id}>
                                <TableCell>{c.compensationType}</TableCell>
                                <TableCell>{Number(c.amount).toLocaleString()}</TableCell>
                                <TableCell>{c.currency}</TableCell>
                                <TableCell>{c.effectiveFrom ? new Date(c.effectiveFrom).toLocaleDateString() : '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </Box>
                  )}
                </Box>

                <Divider />

                {/* Contract Documents Section */}
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">Contract Documents</Typography>
                    <Button size="small" variant="outlined" startIcon={<Upload />} onClick={() => { setUploadCategory('contract'); setUploadOpen(true); }}>Upload</Button>
                  </Box>
                  {contractDocs.length === 0 ? (
                    <Box textAlign="center" py={3} color="text.secondary">
                      <Gavel sx={{ fontSize: 40, opacity: 0.3 }} />
                      <Typography mt={1} variant="body2">No contract documents uploaded yet</Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {contractDocs.map((doc, i) => (
                        <React.Fragment key={doc.id}>
                          {i > 0 && <Divider />}
                          <ListItem sx={{ py: 1.5, '&:hover': { bgcolor: '#f9f9f9' } }}>
                            <ListItemIcon><Gavel sx={{ color: '#7b1fa2' }} /></ListItemIcon>
                            <ListItemText
                              primary={doc.originalName}
                              secondary={`${doc.description ? doc.description + ' · ' : ''}${formatSize(doc.fileSize)} · ${new Date(doc.createdAt).toLocaleDateString()}`}
                            />
                            <ListItemSecondaryAction>
                              <Tooltip title="Download"><IconButton size="small" onClick={() => handleDownload(doc.id, doc.originalName)}><Download fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(doc.id, doc.originalName)}><Delete fontSize="small" /></IconButton></Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Box>
              </Box>
            );
          })()}
        </Box>
      </Paper>

      {/* Contract Dialog */}
      <Dialog open={contractDialogOpen} onClose={() => !savingContract && setContractDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingContract ? 'Edit Contract' : 'Add Contract'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Contract Type</InputLabel>
            <Select value={contractForm.contractType} onChange={e => setContractForm(f => ({ ...f, contractType: e.target.value }))} label="Contract Type">
              {[['full_time','Full Time'],['part_time','Part Time'],['contract','Contract'],['intern','Intern']].map(([v,l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </Select>
          </FormControl>
          <Grid container spacing={2}>
            <Grid size={6}>
              <TextField fullWidth label="Start Date" type="date" value={contractForm.startDate} onChange={e => setContractForm(f => ({ ...f, startDate: e.target.value }))} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="End Date" type="date" value={contractForm.endDate} onChange={e => setContractForm(f => ({ ...f, endDate: e.target.value }))} InputLabelProps={{ shrink: true }} helperText="Leave blank if ongoing" />
            </Grid>
            <Grid size={4}>
              <TextField fullWidth label="Weekly Hours" type="number" value={contractForm.weeklyHours} onChange={e => setContractForm(f => ({ ...f, weeklyHours: Number(e.target.value) }))} />
            </Grid>
            <Grid size={4}>
              <TextField fullWidth label="Probation (months)" type="number" value={contractForm.probationMonths} onChange={e => setContractForm(f => ({ ...f, probationMonths: Number(e.target.value) }))} />
            </Grid>
            <Grid size={4}>
              <TextField fullWidth label="Notice Period (days)" type="number" value={contractForm.noticePeriodDays} onChange={e => setContractForm(f => ({ ...f, noticePeriodDays: Number(e.target.value) }))} />
            </Grid>
          </Grid>
          {savingContract && <LinearProgress />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractDialogOpen(false)} disabled={savingContract}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveContract} disabled={savingContract || !contractForm.startDate}>{savingContract ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Compensation Dialog */}
      <Dialog open={compDialogOpen} onClose={() => !savingComp && setCompDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingComp ? 'Edit Compensation' : 'Add Compensation Package'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select value={compForm.compensationType} onChange={e => setCompForm(f => ({ ...f, compensationType: e.target.value }))} label="Type">
              <MenuItem value="salary">Salary (Monthly)</MenuItem>
              <MenuItem value="hourly">Hourly Rate</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Currency</InputLabel>
            <Select value={compForm.currency} onChange={e => setCompForm(f => ({ ...f, currency: e.target.value }))} label="Currency">
              {CURRENCIES.map(c => <MenuItem key={c.code} value={c.code}>{c.code} — {c.name}{c.symbol ? ` (${c.symbol})` : ''}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label={compForm.compensationType === 'salary' ? 'Monthly Salary' : 'Hourly Rate'}
            type="number"
            value={compForm.amount}
            onChange={e => setCompForm(f => ({ ...f, amount: e.target.value }))}
            required
            InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>{compForm.currency}</Typography> }}
          />
          <TextField fullWidth label="Bonus Target (optional)" type="number" value={compForm.bonusTarget} onChange={e => setCompForm(f => ({ ...f, bonusTarget: e.target.value }))} />
          <TextField fullWidth label="Effective From" type="date" value={compForm.effectiveFrom} onChange={e => setCompForm(f => ({ ...f, effectiveFrom: e.target.value }))} InputLabelProps={{ shrink: true }} required />
          {savingComp && <LinearProgress />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompDialogOpen(false)} disabled={savingComp}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveComp} disabled={savingComp || !compForm.amount || !compForm.effectiveFrom}>{savingComp ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Box
            sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
            onClick={() => document.getElementById('file-input-profile')?.click()}
          >
            <input id="file-input-profile" type="file" hidden onChange={e => setUploadFile(e.target.files?.[0] || null)} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" />
            {uploadFile ? (
              <Box><Description color="primary" sx={{ fontSize: 40 }} />
                <Typography variant="body2" fontWeight="bold">{uploadFile.name}</Typography>
                <Typography variant="caption" color="text.secondary">{formatSize(uploadFile.size)}</Typography>
              </Box>
            ) : (
              <Box><Upload sx={{ fontSize: 40, color: '#ccc' }} />
                <Typography variant="body2" color="text.secondary">Click to select file (PDF, JPG, PNG, DOC, XLS — max 10MB)</Typography>
              </Box>
            )}
          </Box>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select value={uploadCategory} onChange={e => setUploadCategory(e.target.value)} label="Grade">
              {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}><Box display="flex" alignItems="center" gap={1}><Box sx={{ color: c.color }}>{c.icon}</Box>{c.label}</Box></MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Description (optional)" value={uploadDescription} onChange={e => setUploadDescription(e.target.value)} fullWidth multiline rows={2} />
          {uploading && <LinearProgress />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!uploadFile || uploading} startIcon={<Upload />}>{uploading ? 'Uploading...' : 'Upload'}</Button>
        </DialogActions>
      </Dialog>

      {employee && (
        <EditEmployeeModal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onEmployeeUpdated={() => { setIsEditModalOpen(false); window.location.reload(); }}
          employee={employee}
        />
      )}

      {/* ID Card Dialog */}
      <Dialog open={idCardOpen} onClose={() => setIdCardOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <CreditCard color="primary" />
              <span>Employee ID Card — {displayName(employee)}</span>
            </Box>
            <IconButton onClick={() => setIdCardOpen(false)} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 3 }}>
          <EmployeeIdCard employee={employee} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EmployeeProfile;
